import React, {useRef,useEffect} from 'react';
import {connect, useSelector} from 'react-redux';
import BootstrapTable from 'react-bootstrap-table-next';
import {
    getInvoiceQuery,
    getSelectedInvoiceKeys,
    getSelectedPrimaryAccount,
    getSelectedSecondaryAccount
} from '../../selectors/Selectors';
import {addInvoiceToSelected, removeInvoiceFromSelected} from '../../actions/InvoiceActions';
import _ from 'lodash';
import {FormattedMessage, useIntl} from "react-intl";
import {Invoice, InvoiceWithKey} from "../../interfaces/invoice";
import {AccountType, Account} from "../../interfaces/Accounts";
import {getInvoiceKey} from "../../utils";
import {useHasMultiCurrency} from "./CurrencySelector";
import {useInvoiceTotal} from "../../hooks/InvoiceHooks";
import {RootState} from "../../reducers/RootReducer";
import {InvoiceCard} from "./InvoiceCard";
import {MultiSelect} from "../../common/MultiSelect";
import {useMediaQuery} from 'react-responsive'
import {buildColumnConfigs} from "./InvoiceTableObject";
import ToolkitProvider, {CSVExport} from 'react-bootstrap-table2-toolkit';
import {DownloadExcelButton, DownloadPdfZipButton} from "./DownloadPdfZipButton";
import {DropdownButton, Dropdown} from "react-bootstrap";
import {useCanMakePayments, useCurrencyDecimalLookup} from "../../selectors/ConfigSelectors";
import {isPayment} from "../../common/invoiceHelpers";

const {ExportCSVButton} = CSVExport;


interface TableConfig {
    columns: { [key: string]: string }[];
}

interface IComponentOwnProps {
    isSearch: boolean;
}

interface IComponentStoreProps {
    accountType: AccountType;
    secondaryAccounts: Account[];
    selectedPrimaryAccount: Account;
    invoices: Invoice[];
    selectedInvoicesKeys: string[];
    tableConfig: TableConfig;
    selectedSoldTos: string[];
}

interface InvoiceSearchComponentOwnProps {
    isSearch: boolean;
    invoices: Invoice[];
    invoiceSearchQuery: any;
}

interface InvoiceListSearchProps {
    accountType: any;
    secondaryAccounts: any;
    selectedPrimaryAccount: any;
    selectedInvoicesKeys: string[];
    tableConfig: TableConfig;
    selectedSoldTos: string[];
}

interface IComponentDispatchProps {
    addInvoiceToSelected: any;
    removeInvoiceFromSelected: any;
}

type IComponentProps = IComponentOwnProps & IComponentStoreProps & IComponentDispatchProps

interface InvoiceListInternalProps {
    isSearch: boolean;
}

function _InvoiceList(props) {
    const hasMultiCurrency = useHasMultiCurrency();
    const selectedCurrency = useSelector<RootState, string>(state => state.default.selectedCurrencyOptionValue);
    const canMakePayment = useCanMakePayments();
    const decimalLookup = useCurrencyDecimalLookup();
    
    function onRowSelect(row:Invoice, isSelected) {
        if (isSelected) {
            props.addInvoiceToSelected(row, decimalLookup(row.currency_key));
        } else {
            props.removeInvoiceFromSelected(row, decimalLookup(row.currency_key));
        }
    }

    function renderHeaderCheckbox({mode, checked, indeterminate}) {
        return (<div className="column-header text-center" style={{}}>
            <span><FormattedMessage id={"invoice_table.pay"}/></span></div>);
    }


    function onSelectAll(isSelect, rows, e) {
        rows.forEach(row => {
            if (isSelect) {
                props.addInvoiceToSelected(row, decimalLookup(row.currency_key));
            } else {
                props.removeInvoiceFromSelected(row, decimalLookup(row.currency_key));
            }
        });
    }

    function clickSelectAll(e) {
        props.invoices.filter((invoice) => !isNotSelectable(invoice))
            .filter(filterFilteredInvoices)
            .forEach(invoice => {
                props.addInvoiceToSelected(invoice, decimalLookup(invoice.currency_key));
            });
    }

    function filterFilteredInvoices(invoice) {
        if (hasMultiCurrency && props.selectedCurrency && props.selectedCurrency !== invoice.currency_key) {
            return false;
        }
        if (props.accountType === AccountType.Payer || props.accountType === AccountType.PayerAll) {
            return props.selectedSoldTos.indexOf(_.trimStart(invoice.soldto_number, '0')) !== -1;
        }
        return true;
    }

    function clickDeselectAll(e) {
        props.invoices.filter((invoice) => !isNotSelectable(invoice))
            .filter(filterFilteredInvoices)
            .forEach(invoice => {
                props.removeInvoiceFromSelected(invoice);
            });
    }

    function isNotSelectable(invoice: Invoice) {
        if (isPayment(invoice) ||
            (hasMultiCurrency && invoice.currency_key !== props.selectedCurrency)
        ) {
            return true;
        }
        return props.isSearch;
    }

    function hideSelect(): boolean {
        return props.isSearch || !canMakePayment;
    }


    const selectRowProp = {
        mode: 'checkbox',
        onSelect: onRowSelect,
        onSelectAll: onSelectAll,
        selected: props.selectedInvoicesKeys,
        selectionRenderer: MultiSelect,
        selectionHeaderRenderer: renderHeaderCheckbox,
        nonSelectable: props.invoices.filter((invoice) => isNotSelectable(invoice)).map(invoice => {
            return getInvoiceKey(invoice);
        }),
        hideSelectColumn: hideSelect(),
    };
    const tab = useRef('table');
    const top = 'Top';


    let invoices = props.invoices;
    if (!props.isSearch) {
        invoices = props.invoices.filter(filterFilteredInvoices);
    }
    const intl = useIntl();
    
    let columns = buildColumnConfigs(intl, props.tableConfig);

    let invoicesWithKeys: InvoiceWithKey[] = invoices.map((_: any) => {
        _._key = getInvoiceKey(_);
        return _ as InvoiceWithKey;
    });

    return (
        <React.Fragment>
            <ToolkitProvider
                data={invoicesWithKeys}
                ref={tab}
                headerStyle={{background: '#DFECF5', height: '40px', borderCollapse: 'collapse'}}
                classes={'wa-bootstrap-table-container custom-table-header'}
                scrollTop={top}
                columns={columns}
                keyField={'_key'}
                bootstrap4={true}
                exportCSV={{
                    noAutoBOM: {autoBom: true},
                }}
            >
                {
                    toolkitProps => (
                        <>
                            <div className="row py-2">
                                <div className="col-12 col-md d-flex align-items-end order-2 order-md-0">
                                    {!hideSelect() &&
                                    <div><a href="#" onClick={clickSelectAll}><FormattedMessage
                                      id={"invoice_table.select_all"}/></a> / <a
                                      href="#"
                                      onClick={clickDeselectAll}><FormattedMessage
                                      id={"invoice_table.deselect_all"}/></a></div>}
                                </div>
                                {hasMultiCurrency && props.selectedCurrency &&
                                <div
                                  className={"col-12 col-md d-flex justify-content-center  align-items-baseline"}>
                                    <span className={"invoice-currency-warning"}>
                                        <FormattedMessage id={"invoices_page.currency_warning"}
                                            values={{"currency": selectedCurrency}}
                                        />
                                    </span>
                                </div>}
                                <div className={"col d-flex justify-content-end  align-items-baseline order-1 order-md-0"}>
                                    <DropdownButton
                                        alignRight
                                        size={"sm"}
                                        title={intl.formatMessage({id: "invoice_export.download_label"})}
                                        variant={"light"}
                                        id="dropdown-menu-align-right"
                                    >
                                        <DownloadPdfZipButton invoiceSearchQuery={props.invoiceSearchQuery}/>
                                    </DropdownButton>
                                    <DropdownButton
                                        alignRight
                                        size={"sm"}
                                        title={intl.formatMessage({id: "invoice_export.export_label"})}
                                        variant={"light"}
                                        id="dropdown-menu-align-right"
                                        className={"pl-2"}
                                    >
                                        <Dropdown.Item onClick={(e) => {
                                            e.preventDefault();
                                            toolkitProps.csvProps.onExport();
                                        }}>{intl.formatMessage({id: "invoice_export.all_csv"})}</Dropdown.Item>
                                        <DownloadExcelButton invoiceSearchQuery={props.invoiceSearchQuery}/>
                                    </DropdownButton>
                                </div>
                            </div>
                            {/*{isMobile && invoices.map((invoice) => (*/}
                            {/*    <InvoiceCard invoice={invoice}/>*/}
                            {/*))}*/}
                            {<div className="row">
                                <div className="col-12">                                   
                                    <BootstrapTable
                                        {...toolkitProps.baseProps}
                                        defaultSorted={[{dataField: "due_date", order: 'desc'}]}
                                        selectRow={selectRowProp}
                                        classes={'wa-bootstrap-table-container custom-table-header'}
                                    >
                                    </BootstrapTable>

                                </div>

                            </div>}
                        </>
                    )
                }
            </ToolkitProvider>
        </React.Fragment>
    );
}

export const InvoiceList = connect<IComponentStoreProps, IComponentDispatchProps, IComponentOwnProps, any>((state, props) => {
    return {
        selectedPrimaryAccount: getSelectedPrimaryAccount(state),
        selectedSecondaryAccount: getSelectedSecondaryAccount(state),
        invoices: state.default.invoices,
        selectedInvoicesKeys: getSelectedInvoiceKeys(state),
        selectedSoldTos: state.user_options.selectedSoldTos,
        secondaryAccounts: state.user_options.secondaryAccounts,
        accountType: state.user_options.accountType,
        tableConfig: state.app.config.invoice_table,
        language: state.user.language,
        selectedCurrency: state.default.selectedCurrencyOptionValue,
        invoiceSearchQuery: getInvoiceQuery(state)
    };
}, {
    addInvoiceToSelected: addInvoiceToSelected,
    removeInvoiceFromSelected: removeInvoiceFromSelected,
})(_InvoiceList);

export var InvoiceSearchList = connect<InvoiceListSearchProps, IComponentDispatchProps, InvoiceSearchComponentOwnProps, any>((state, props) => {
    return {
        selectedPrimaryAccount: getSelectedPrimaryAccount(state),
        selectedSecondaryAccount: getSelectedSecondaryAccount(state),
        selectedInvoicesKeys: getSelectedInvoiceKeys(state),
        selectedSoldTos: state.user_options.selectedSoldTos,
        secondaryAccounts: state.user_options.secondaryAccounts,
        accountType: state.user_options.accountType,
        tableConfig: state.app.config.invoice_table,
        language: state.user.language,
        selectedCurrency: state.default.selectedCurrencyOptionValue,
    };
}, {
    addInvoiceToSelected: addInvoiceToSelected,
    removeInvoiceFromSelected: removeInvoiceFromSelected,
})(_InvoiceList);
